import SectionHeading from "../ui/SectionHeading";

type Project = {
  image: string;
  imageWidth?: number;
  imageHeight?: number;
  title: string;
  description: string;
  technologies?: string[];
  demo?: string;
  code?: string;
  contributions?: string[];
  contribution?: string[];
};

const getContributions = (project: Project) =>
  project.contributions ?? project.contribution ?? [];

const ProjectLinks = ({ project }: { project: Project }) => (
  <div className="project-story__links">
    {project.demo && (
      <a
        href={project.demo}
        target="_blank"
        rel="noreferrer"
        data-cursor="Visit"
      >
        Live project <span aria-hidden="true">↗</span>
      </a>
    )}
    {project.code && (
      <a
        href={project.code}
        target="_blank"
        rel="noreferrer"
        data-cursor="Code"
      >
        View code <span aria-hidden="true">↗</span>
      </a>
    )}
  </div>
);

const ProjectShowcase = ({ projects }: { projects: Project[] }) => (
  <section className="projects-section section-shell" id="projects">
    <SectionHeading
      index="03"
      label="Selected work / archive"
      title="Products built for real people and real operations."
    />

    <div className="project-showcase">
      <div className="project-showcase__visual-column">
        <div className="project-showcase__sticky">
          <div className="project-visuals">
            {projects.map((project, index) => (
              <figure
                className={`project-visual${index === 0 ? " is-active" : ""}`}
                data-project-index={index}
                key={project.title}
                aria-hidden={index !== 0}
              >
                <img
                  src={project.image}
                  alt=""
                  width={project.imageWidth ?? 1600}
                  height={project.imageHeight ?? 900}
                  loading="lazy"
                  decoding="async"
                />
              </figure>
            ))}
          </div>

          <div className="project-showcase__chrome" aria-hidden="true">
            <span>Selected projects</span>
            <span className="project-progress">
              <strong className="project-progress__current">01</strong>
              <span>/</span>
              <span>{String(projects.length).padStart(2, "0")}</span>
            </span>
          </div>
        </div>
      </div>

      <div className="project-stories">
        {projects.map((project, index) => {
          const contributions = getContributions(project);

          return (
            <article
              className="project-story"
              data-project-index={index}
              key={project.title}
            >
              <div className="project-story__mobile-image" data-reveal>
                <img
                  src={project.image}
                  alt={`${project.title} project preview`}
                  width={project.imageWidth ?? 1600}
                  height={project.imageHeight ?? 900}
                  loading="lazy"
                  decoding="async"
                />
              </div>

              <div className="project-story__meta">
                <span>{String(index + 1).padStart(2, "0")}</span>
                <span>
                  {project.technologies?.slice(0, 2).join(" / ") ??
                    "Software development"}
                </span>
              </div>
              <h3>{project.title}</h3>
              <p className="project-story__description">
                {project.description}
              </p>

              {contributions.length > 0 && (
                <div className="project-story__contributions">
                  <span>Selected contributions</span>
                  <ul>
                    {contributions.map((contribution) => (
                      <li key={contribution}>{contribution}</li>
                    ))}
                  </ul>
                </div>
              )}

              {project.technologies && (
                <ul
                  className="project-story__tech"
                  aria-label="Technology stack"
                >
                  {project.technologies.map((technology) => (
                    <li key={technology}>{technology}</li>
                  ))}
                </ul>
              )}

              <ProjectLinks project={project} />
            </article>
          );
        })}
      </div>
    </div>
  </section>
);

export default ProjectShowcase;
